import React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import Text from 'components/text';
import TextResizer from 'components/text_resizer';
import { DeckUI } from '@seedlang/state';
import { pixify, isPresent, isBlank } from '@seedlang/utils';
import { Theme } from '@seedlang/constants';
import { flexCenter, flexCenterColumn } from '@seedlang/style_mixins';
import WordBreakdown from 'components/word_breakdown';
import Answer from 'components/vocab_card/answer';
import GrammarHint from 'components/grammar_hint';
import ExampleSentence, { ExampleSentenceToggle } from './example_sentence';
import { ExampleSentenceTranslationProvider } from '@seedlang/hoc';

const Wrapper = styled.div`
  width: 100%;
  ${flexCenterColumn};
  height: ${props => props.height};
  position: ${props => props.hasExample ? 'relative' : undefined};
`;

const ExampleSentenceFooter = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  max-height: calc(50% - 40px);
  overflow-y: auto;
`;

const WrongAnswerSlot = styled.div`
  position: relative;
  width: 100%;
  height: 50px;
  flex-shrink: 0;
  align-items: center;
  display: flex;
  justify-content: center;
`;

const CenteredContent = styled.div`
  width: 100%;
  flex: 1;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const PrimaryTextWrapper = styled.div`
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  flex: ${props => props.hasExample ? 0 : 1};
`;

const Grammar = styled.div`
  font-size: ${props => props.fontSize};
  font-weight: ${props => props.fontWeight};
  color: ${props => props.color};
  font-style: italic;
`;

const CorrectAnswer = styled.div`
  position: absolute;
  bottom: 10px;
  background: ${Theme.green};
  border-radius: 10px;
  width: 96%;
  ${flexCenter()}
  overflow: hidden;
  height: 40px;
  i {
    position: absolute;
    left: 6px;
    top: 3px;
    color: white;
  }
`;


const ConjugatedVerbsWrapper = styled.div`
`;

const IntroText = styled.div`
  font-size: 14px;
  margin-bottom: 5px;
  color: #FFF;
  padding: 4px 8px;
  border-radius: 5px;
  background: ${Theme.green};
`;

@observer
class ExpandedTarget extends React.Component {

  static defaultProps = {
    grammarTextSize: 14,
    grammarFontWeight: null,
    grammarColor: '#333',
    color: '#000',
  };

  render() {
    const hasExample = Boolean(DeckUI.showVocabExampleSentences && (this.props.card?.vocabWordComprehendCard || this.props.card?.vocabWordTranslateCard || this.props.card?.wordOptionsCard) && this.props.word?.cardExampleSentence);
    const hasWrongAnswer = Boolean(this.props.cardState?.revealed && this.props.showWrongAnswer && isPresent(this.props.wrongAnswer));
    const showExample = hasExample && (this.props.cardState?.revealed || (this.props.card?.wordOptionsCard && this.props.sentenceHintShown));
    const showHint = hasExample && this.props.card?.wordOptionsCard && !this.props.cardState?.revealed && !this.props.sentenceHintShown;
    const Content = hasExample || hasWrongAnswer ? CenteredContent : React.Fragment;
    return (
      <ExampleSentenceTranslationProvider
        parts={this.props.word?.cardExampleSentence}
        translation={this.props.word?.cardExampleTranslation}
        languageId={this.props.word?.languageId}
        revealed={this.props.cardState?.revealed}
        visible={showExample}
      >
      <Wrapper hasExample={hasExample || hasWrongAnswer} height={hasExample || hasWrongAnswer ? '100%' : undefined}>
        <ExampleSentenceToggle inset={-5} showHint={showHint} onShowHint={this.props.showSentenceHint} />
        <Content>
          {
            this.props.showVocabTriadIntro &&
              <IntroText>
                <span role='img' aria-label='lightbulb'>💡</span> In this deck we will teach you...
              </IntroText>
          }
          {
            this.props.prompt &&
            <Text
              italic
              fontSize='16px'
            >
              {this.props.prompt}
            </Text>
          }
          <PrimaryTextWrapper hasExample={hasExample}>
            {
              this.props.word &&
                <TextResizer
                  height={60}
                  width={pixify(DeckUI.layout.deckWidth - 20)}
                  minimumFontSize={14}
                  resizeText
                  characterLength={this.props.word.targetTextWithDefiniteArticle && this.props.word.targetTextWithDefiniteArticle.length}
                >
                  <Text
                    bold
                    color={this.props.color}
                  >
                    {isBlank(this.props.card?.underlinedCharacters) && (this.props.hideDefiniteArticle ? this.props.word.targetText : this.props.word.targetTextWithDefiniteArticle)}
                    {!isBlank(this.props.card?.underlinedCharacters) && this.props.card.vocabWordComprehendCard &&
                      <WordBreakdown
                        card={this.props.card}
                      />
                    }
                  </Text>
                </TextResizer>
            }
          </PrimaryTextWrapper>

          {
            !this.props.hidePlural && this.props.word && this.props.word.isNoun && this.props.word.pluralNoun && !this.props.word.noPlural &&
              <Grammar
                color={this.props.grammarColor}
                fontSize={pixify(DeckUI.layout.minimumHeight ? '13px' : this.props.grammarTextSize)}
                fontWeight={this.props.grammarFontWeight}
              >
                {this.props.word.pluralNounsString}
              </Grammar>
          }
          {
            !this.props.hidePlural && this.props.word && this.props.word.isNoun && this.props.word.noPlural &&
              <Grammar
                color={this.props.grammarColor}
                fontSize={pixify(DeckUI.layout.minimumHeight ? '13px' : this.props.grammarTextSize)}
                fontWeight={this.props.grammarFontWeight}
              >
                No Plural
              </Grammar>
          }
          {
            this.props.word && (this.props.word.isAdjective || this.props.word.isNumber) && this.props.word.genderVariant && this.props.word.genderVariant.targetText !== this.props.word.targetText &&
            <Grammar
              color={this.props.grammarColor}
              fontSize={pixify(DeckUI.layout.minimumHeight ? '13px' : this.props.grammarTextSize)}
              fontWeight={this.props.grammarFontWeight}
            >
              {this.props.word.genderVariant.targetText}
            </Grammar>
          }
          {
            !this.props.hideConjugation && this.props.word && this.props.word.isVerb && this.props.card && this.props.word.conjugatedVerbStringsCount > 0 && this.props.word.hasConjugatedVerbStrings &&
              <Grammar
                color={this.props.grammarColor}
                fontSize={pixify(DeckUI.layout.minimumHeight ? '13px' : this.props.grammarTextSize)}
                fontWeight={this.props.grammarFontWeight}
              >
                <ConjugatedVerbsWrapper>
                  {this.props.word.conjugatedVerbStringsSummary}
                </ConjugatedVerbsWrapper>
              </Grammar>
          }
          <GrammarHint
            description={this.props.description}
            backgroundColor={this.props.descriptionBackgroundColor}
            textColor={this.props.descriptionTextColor}
            italic={this.props.descriptionItalic}
            margin={this.props.descriptionMargin}
            tooltipId={this.props.word?.id}
            hideIcon={this.props.hideDescriptionIcon}
          />
          {
            this.props.word && this.props.word.isPhoneme && this.props.card && isBlank(this.props.prompt) &&
              <Grammar
                color={this.props.grammarColor}
                fontSize={pixify(DeckUI.layout.minimumHeight ? '13px' : this.props.grammarTextSize)}
                fontWeight={this.props.grammarFontWeight}
              >
                This is how we write down this sound
              </Grammar>
          }
          {
            this.props.word && this.props.word.isGrapheme && this.props.card && isBlank(this.props.prompt) &&
              <Grammar
                color={this.props.grammarColor}
                fontSize={pixify(DeckUI.layout.minimumHeight ? '13px' : this.props.grammarTextSize)}
                fontWeight={this.props.grammarFontWeight}
              >
                {`Listen to the pronunciation of this letter${this.props.word.targetText.length > 1 ? ' combination' : null}`}
              </Grammar>
          }
          {this.props.children}
          {
            DeckUI.showCorrectAnswer && isPresent(this.props.cardState.textEntry) &&
            <CorrectAnswer>
              <i className='fa fa-times' />
              <TextResizer
                resizeText
                height={60}
                padding={10}
                maximumFontSize={24}
                width={DeckUI.layout.deckWidth - 40}
                characterLength={this.props.cardState.textEntry && this.props.cardState.textEntry.length}
              >
                <Text
                  bold
                  color='#FFF'
                >
                  {this.props.cardState.textEntry}
                </Text>
              </TextResizer>
            </CorrectAnswer>
          }
        </Content>
        {(showExample || hasWrongAnswer) &&
          <ExampleSentenceFooter>
            {showExample &&
              <ExampleSentence
                key={this.props.card.id}
                parts={this.props.word.cardExampleSentence}
                translation={this.props.word.cardExampleTranslation}
                languageId={this.props.word.languageId}
                revealed={this.props.cardState?.revealed}
                fontSize={DeckUI.layout.minimumHeight ? 13 : this.props.grammarTextSize}
                color={this.props.grammarColor}
              />
            }
            {hasWrongAnswer &&
              <WrongAnswerSlot>
                <Answer text={this.props.wrongAnswer} marginBottom="10px" />
              </WrongAnswerSlot>
            }
          </ExampleSentenceFooter>
        }
      </Wrapper>
      </ExampleSentenceTranslationProvider>
    );
  }
}

export default ExpandedTarget;
